import { getAddressInfo } from "bitcoin-address-validation";

// Checksum validation for Bitcoin addresses, so a typo'd address is reported as
// invalid instead of "valid" on structure alone.
//
// Base58Check (1…, 3…, m…, n…, 2…) goes through bitcoin-address-validation (double
// SHA-256 checksum + version byte). Segwit (bc1…, tb1…) is decoded here per BIP-173 /
// BIP-350, because that library picks bech32 vs bech32m with a case-sensitive prefix
// test, doesn't tie witness version to checksum variant, and accepts any program
// length as P2WSH.

const BASE58_ALPHABET =
  "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

/** Decoded byte length of a base58 string, or null if it has a non-base58 char. */
export function base58DecodedLength(input: string): number | null {
  const bytes: number[] = [];
  for (const ch of input) {
    let carry = BASE58_ALPHABET.indexOf(ch);
    if (carry < 0) return null;
    for (let i = 0; i < bytes.length; i++) {
      carry += bytes[i] * 58;
      bytes[i] = carry & 0xff;
      carry >>= 8;
    }
    while (carry > 0) {
      bytes.push(carry & 0xff);
      carry >>= 8;
    }
  }
  let leadingZeros = 0;
  while (leadingZeros < input.length && input[leadingZeros] === "1") {
    leadingZeros++;
  }
  return bytes.length + leadingZeros;
}

export type Base58Result =
  | { ok: true; type: "p2pkh" | "p2sh"; network: "mainnet" | "testnet" }
  | { ok: false };

export function checkBase58Address(addr: string): Base58Result {
  try {
    const info = getAddressInfo(addr);
    if (info.bech32) return { ok: false };
    if (info.type !== "p2pkh" && info.type !== "p2sh") return { ok: false };
    if (info.network !== "mainnet" && info.network !== "testnet") {
      return { ok: false };
    }
    return { ok: true, type: info.type, network: info.network };
  } catch {
    return { ok: false };
  }
}

// ─── Bech32 / Bech32m (BIP-173, BIP-350) ───────────────────────────

const BECH32_ALPHABET = "qpzry9x8gf2tvdw0s3jn54khce6mua7l";
const BECH32_CONST = 1;
const BECH32M_CONST = 0x2bc830a3;
const GENERATOR = [0x3b6a57b2, 0x26508e6d, 0x1ea119fa, 0x3d4233dd, 0x2a1462b3];

function polymod(values: number[]): number {
  let chk = 1;
  for (const v of values) {
    const top = chk >>> 25;
    chk = ((chk & 0x1ffffff) << 5) ^ v;
    for (let i = 0; i < 5; i++) {
      if ((top >>> i) & 1) chk ^= GENERATOR[i];
    }
  }
  return chk >>> 0;
}

function hrpExpand(hrp: string): number[] {
  const out: number[] = [];
  for (let i = 0; i < hrp.length; i++) out.push(hrp.charCodeAt(i) >> 5);
  out.push(0);
  for (let i = 0; i < hrp.length; i++) out.push(hrp.charCodeAt(i) & 31);
  return out;
}

function convert5to8(words: number[]): number[] | null {
  let acc = 0;
  let bits = 0;
  const out: number[] = [];
  for (const w of words) {
    // keep only the bits still needed (5 + 8 - 1), or JS's 32-bit shifts overflow
    acc = ((acc << 5) | w) & 0xfff;
    bits += 5;
    while (bits >= 8) {
      bits -= 8;
      out.push((acc >> bits) & 0xff);
    }
  }
  // BIP-173: no more than 4 bits of padding, and the padding must be zero
  if (bits >= 5 || ((acc << (8 - bits)) & 0xff) !== 0) return null;
  return out;
}

export type SegwitResult =
  | {
      ok: true;
      network: "mainnet" | "testnet";
      version: number;
      programLength: number;
    }
  | { ok: false; reason: string };

/** Full BIP-173/350 decode of a bc1…/tb1… address. */
export function decodeSegwitAddress(addr: string): SegwitResult {
  if (addr !== addr.toLowerCase() && addr !== addr.toUpperCase()) {
    return { ok: false, reason: "mixes upper- and lower-case letters" };
  }
  const lower = addr.toLowerCase();
  if (lower.length > 90) return { ok: false, reason: "is too long" };

  const sep = lower.lastIndexOf("1");
  const hrp = lower.slice(0, sep);
  const network =
    hrp === "bc" ? "mainnet" : hrp === "tb" ? "testnet" : null;
  if (!network) return { ok: false, reason: "has an unknown prefix" };

  const data: number[] = [];
  for (const ch of lower.slice(sep + 1)) {
    const v = BECH32_ALPHABET.indexOf(ch);
    if (v < 0) {
      return { ok: false, reason: `contains "${ch}", which is not allowed` };
    }
    data.push(v);
  }
  if (data.length < 7) return { ok: false, reason: "is too short" };

  const version = data[0];
  if (version > 16) return { ok: false, reason: "has an invalid witness version" };

  // v0 uses bech32 (BIP-173); v1+ use bech32m (BIP-350)
  const expected = version === 0 ? BECH32_CONST : BECH32M_CONST;
  if (polymod(hrpExpand(hrp).concat(data)) !== expected) {
    return { ok: false, reason: "fails its checksum (likely a typo)" };
  }

  const program = convert5to8(data.slice(1, -6));
  if (!program || program.length < 2 || program.length > 40) {
    return { ok: false, reason: "has an invalid witness program" };
  }
  if (version === 0 && program.length !== 20 && program.length !== 32) {
    return { ok: false, reason: "has an invalid length for a SegWit v0 address" };
  }
  return { ok: true, network, version, programLength: program.length };
}
